import { ContentCopy as ContentCopyIcon, MoreVert as MoreVertIcon } from 'components/icons';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { isPlainLeftClick } from 'components/global-search/GlobalSearchRow';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import useAuth from 'hooks/useAuth.ts';
import { FC } from 'react';
import { dispatch, useSelector } from 'store';
import { useDeleteTimecardMutation } from 'store/api/timecards';
import { resetTimecardState } from 'store/reducers/timecard';
import { MatterStateType } from 'types/matters.ts';
import { TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import Blocked from '../matters/Blocked';
import AddExpenseIconButton from './buttons/AddExpenseIconButton';
import FinishTimecardIconButton from './buttons/FinishTimecardIconButton';
import TimecardTrackingButton from './buttons/TimecardTrackingButton';
import { isOwnDraftOnClosedCase } from './list/ClosedCaseDraftMarker';

type Props = {
    timecard: TimecardListItemData;
    showDuplicateAction?: boolean;
};

/** Anchors, so middle-click works; styled like `CallForActionButton`, which cannot render as an anchor. */
export const ACTION_LINK_CLASS =
    'flex size-8 shrink-0 items-center justify-center rounded-full bg-info-darker/10 text-info-darker hover:bg-info-darker hover:text-paper';

/**
 * Whether the expense action applies to this timecard at all — the status half of the test, which the
 * caller pairs with the CREATE.EXPENSES permission. Exported because the list reserves this button's
 * slot when it is absent, so its icons keep the same position from row to row.
 */
export const isExpensableTimecard = (timecard: TimecardListItemData): boolean =>
    [TimecardStatusType.DRAFT, TimecardStatusType.FINISHED].includes(
        timecard.timecardStatusTypeCode as TimecardStatusType
    );

type MenuItemType = {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
    className?: string;
};

type ActionButtonsProps = {
    timecard: TimecardListItemData;
    isCurrentlyBlocked: boolean;
    isCaseClosed: boolean;
    isDraftTimecard: boolean;
    hasCreateExpensePermission: boolean;
    showDuplicate: boolean;
    onDuplicate: () => void;
    userId: number | undefined;
};

const ActionButtons: FC<ActionButtonsProps> = ({
    timecard,
    isCurrentlyBlocked,
    isCaseClosed,
    isDraftTimecard,
    hasCreateExpensePermission,
    showDuplicate,
    onDuplicate,
    userId,
}) => {
    if (isCurrentlyBlocked) {
        // Drawn in its neighbours' round 32px footprint with a 20px glyph; the case lists keep the badge's own shape.
        return (
            <Blocked tooltipText="dashboard.tooltips.timecard-blocked-short" className="size-8 rounded-full p-1.5" />
        );
    }

    if (isCaseClosed && isDraftTimecard) {
        // The row's leading marker already explains this to the draft's creator.
        return isOwnDraftOnClosedCase(timecard, userId) ? null : (
            <InfoTooltip text="timecards.tooltips.connected-case-closed" color="primary" />
        );
    }

    return (
        <>
            {isExpensableTimecard(timecard) && hasCreateExpensePermission && (
                <AddExpenseIconButton timecard={timecard} />
            )}

            {showDuplicate ? (
                <Tooltip content={t('timecards.duplicate')}>
                    <LinkWithOrigin
                        to={`/timecards/duplicate/${timecard.id}`}
                        aria-label={t('timecards.duplicate')}
                        className={ACTION_LINK_CLASS}
                        // A plain click goes the menu entry's way, which opens the create dialog where there is
                        // one. The anchor is what a new tab takes.
                        onClick={(event) => {
                            if (isPlainLeftClick(event)) {
                                event.preventDefault();
                                onDuplicate();
                            }
                        }}
                    >
                        <ContentCopyIcon className="text-[20px]" />
                    </LinkWithOrigin>
                </Tooltip>
            ) : null}

            {userId === timecard.employee.id && timecard.timecardStatusTypeCode === TimecardStatusType.DRAFT && (
                <>
                    <FinishTimecardIconButton timecard={timecard} />
                    <TimecardTrackingButton timecard={timecard} />
                </>
            )}
        </>
    );
};

const TimecardActionButtons = ({ timecard, showDuplicateAction }: Props): JSX.Element => {
    const { openModal, closeModal } = useModal();

    const hasCreateExpensePermission = useHasPermissions(['CREATE.EXPENSES']);
    const hasCreateTimecardPermission = useHasPermissions(['CREATE.TIMECARDS']);
    const { user } = useAuth();
    const userId = user?.id;
    const activeTenantId = user?.activeTenantId;

    const { trackedTimecardId } = useSelector((store) => store.timecard);
    const isTrackedTimecard = trackedTimecardId && parseInt(trackedTimecardId) === timecard.id;
    const { matter } = timecard;
    const isCaseClosed =
        matter.matterStateTypeCode === MatterStateType.COMPLETED ||
        matter.matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matter.matterStateTypeCode === MatterStateType.OFFER_WON;
    const isDraftTimecard = timecard.timecardStatusTypeCode === TimecardStatusType.DRAFT;
    const canDuplicate =
        hasCreateTimecardPermission && !(isCaseClosed && isDraftTimecard) && !matter.isCurrentlyBlocked;

    const navigateWithReturn = useNavigateWithReturnState();

    const handleDuplicate = () => {
        navigateWithReturn(`/timecards/duplicate/${timecard.id}`);
    };

    const [deleteTimecard] = useDeleteTimecardMutation();

    const handleDeleteTimecard = async () => {
        if (timecard.id && !isNaN(Number(timecard.id))) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        void deleteTimecard({ id: timecard.id, matterId: matter.id })
                            .unwrap()
                            .then(() => {
                                if (isTrackedTimecard && userId && activeTenantId) {
                                    dispatch(resetTimecardState({ userId, tenantId: activeTenantId }));
                                }
                                closeModal();
                            })
                            .catch((e) => {
                                console.error(e);
                                errorAlert(t('forms.error-submitting-the-form'));
                            });
                    }}
                    content={<p className="text-body1">{t('timecards.delete-confirmation-text')}</p>}
                />
            );
        }
    };

    // Where the row already shows the Duplicate icon the menu does not repeat it; the card list has
    // no icon, so there the menu is the only way to duplicate.
    const menuItems: MenuItemType[] = [
        ...(showDuplicateAction
            ? []
            : [
                  {
                      label: t('timecards.duplicate'),
                      onClick: handleDuplicate,
                      isDisabled: !canDuplicate,
                  },
              ]),
        {
            label: t('timecards.delete'),
            className: 'text-fg-error focus:text-fg-error',
            onClick: () => {
                void handleDeleteTimecard();
            },
            isDisabled: !timecard.isDeletable,
        },
    ];

    const visibleMenuItems = menuItems.filter((item) => !item.isDisabled);

    return (
        <>
            <ActionButtons
                key={timecard.id}
                isCurrentlyBlocked={matter.isCurrentlyBlocked}
                hasCreateExpensePermission={hasCreateExpensePermission}
                isCaseClosed={isCaseClosed}
                isDraftTimecard={isDraftTimecard}
                showDuplicate={Boolean(showDuplicateAction) && canDuplicate}
                onDuplicate={handleDuplicate}
                timecard={timecard}
                userId={userId}
            />
            {visibleMenuItems.length === 0 ? (
                // Same footprint as the button, so the action column stays aligned across rows.
                <span aria-hidden className="inline-block size-8 shrink-0" />
            ) : (
                <DropdownMenu>
                    <DropdownMenuTrigger
                        render={
                            <TableRowCallForActionButton shape="rounded" aria-label={t('actions')}>
                                <MoreVertIcon />
                            </TableRowCallForActionButton>
                        }
                    />
                    <DropdownMenuContent align="end" className="w-auto">
                        {visibleMenuItems.map((item) => (
                            <DropdownMenuItem key={item.label} className={item.className} onClick={item.onClick}>
                                {item.label}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </>
    );
};

export default TimecardActionButtons;
